import * as React from "react";

import {
  FilterParams,
  PaginatedParams,
  PaginatedData
} from "src/library/utils";
import useAxios from "src/core/shared/hooks/useAxios";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";
import { IUser } from "src/core/users/api/users.api";
import { IDrop } from "src/core/drops/api/drops.api";
import { ICustomer } from "src/core/customers/api/customers.api";

import {
  UseApiRequest,
  EndpointConfig,
  RequestConfig
} from "../../shared/api/config.api";

export type CreateNotificationParams = Pick<INotification, "body" | "title" > & { notificationRecipients: Array<Partial<INotificationRecipient>> };

export type UpdateNotificationParams = Pick<INotification, "body" | "title" | "notificationRecipients">;
export type ListNotificationsParams = { pagination: PaginatedParams<INotification>; filters: FilterParams<INotification> };
export type ListUserNotificationsParams = ListNotificationsParams & { userId: number };
export type MarkAsReadParams = { userId: number; notificationId: number };
export type ListUserSmartNotificationsParams = { userId: number; branchId: number; };

type NotificationEndpointConfig = {
  LIST_NOTIFICATIONS: EndpointConfig<
    ListNotificationsParams,
    PaginatedData<INotification>
  >;
  GET_NOTIFICATION: EndpointConfig<
    number,
    INotification
  >;
  GET_NOTIFICATION_READING_INFO: EndpointConfig<
    number,
    INotificationReadingInfo
  >;
  GET_NOTIFICATION_RECIPIENTS_INFO: EndpointConfig<
    {
      entityIds: any;
      type: string;
    },
    INotificationRecipientsInfo
  >;
  CREATE_NOTIFICATION: EndpointConfig<
    CreateNotificationParams,
    INotification
  >;
  GET_USER_NOTIFICATIONS: EndpointConfig<
    ListUserNotificationsParams,
    { notifications: PaginatedData<INotification>, unreadNotifications: number }
  >;
  MARK_NOTIFICATION_AS_READ: EndpointConfig<
    MarkAsReadParams,
    INotificationRead
  >;
  DISMISS_NOTIFICATION: EndpointConfig<
    MarkAsReadParams,
    INotificationRead
  >;
  DELETE_NOTIFICATION: EndpointConfig<
    number,
    { message: string; }
  >;
  GET_USER_SMART_NOTIFICATIONS: EndpointConfig<
    ListUserSmartNotificationsParams,
    INotification[]
  >;
};

const requests: RequestConfig<NotificationEndpointConfig> = {
  LIST_NOTIFICATIONS: params => {
    return {
      url: `${process.env.REACT_APP_API_URL}/core/notifications`,
      method: "GET",
      params
    };
  },
  GET_NOTIFICATION: notificationId => ({
    url: `${process.env.REACT_APP_API_URL}/core/notifications/${notificationId}`,
    method: "GET"
  }),
  GET_NOTIFICATION_RECIPIENTS_INFO: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/notifications/recipients/info`,
    method: "GET",
    params
  }),
  GET_NOTIFICATION_READING_INFO: notificationId => ({
    url: `${process.env.REACT_APP_API_URL}/core/notifications/${notificationId}/reading-info`,
    method: "GET"
  }),
  CREATE_NOTIFICATION: data => ({
    url: `${process.env.REACT_APP_API_URL}/core/notifications`,
    method: "POST",
    data
  }),

  DELETE_NOTIFICATION: id => ({
    url: `${process.env.REACT_APP_API_URL}/core/notifications/${id}`,
    method: "DELETE"
  }),
  GET_USER_NOTIFICATIONS: params => {
    const { userId } = params;

    return {
      url: `${process.env.REACT_APP_API_URL}/core/notifications/user/${userId}`,
      method: "GET",
      params: {
        pagination: params.pagination,
        filters: params.filters
      }
    };
  },
  MARK_NOTIFICATION_AS_READ: params => {
    const { userId, notificationId } = params;

    return {
      url: `${process.env.REACT_APP_API_URL}/core/notifications/${notificationId}/user/${userId}/read`,
      method: "PATCH"
    };
  },
  DISMISS_NOTIFICATION: params => {
    const { userId, notificationId } = params;

    return {
      url: `${process.env.REACT_APP_API_URL}/core/notifications/${notificationId}/user/${userId}/dismiss`,
      method: "PATCH"
    };
  },

  GET_USER_SMART_NOTIFICATIONS:
      params => {
        const { userId, branchId } = params;

        return {
          url: `${process.env.REACT_APP_API_URL}/core/notifications/user/${userId}/smart/branch/${branchId}`,
          method: "GET",
          cache: {
            key: () => `smartNotifications-${userId}-${branchId}`,
            maxAge: cacheMinutesToMilliseconds(5),
            clearOnError: true
          }
        };
      }

};

export const useNotificationsApi: UseApiRequest<NotificationEndpointConfig> = (name, config) => {
  return useAxios(requests[ name ], name, config);
};

export interface INotification {
  id: number;
  body: string;
  createdAt: Date;
  notificationReads?: INotificationReads;
  notificationRecipients?: INotificationRecipient[];
  notificationUsers?: INotificationReads;
  notificationUserIds?: number[];
  title: string;
  updatedAt: Date;
  data: {
    orderId?: number;
    listId?: number;
    supportTicketId?: number;
    orderDeliveryDate?: string;
  };
  type: NotificationType;
  sentByUser?: IUser;
  sentByUserId?: number;
}

export interface INotificationRecipient {
  notificationId?: number | null;
  type: INotificationRecipientType;
  entityId?: number;
  entity?: IUser | IDrop | ICustomer
}
export interface INotificationRecipientsInfo {
  customersCount: number;
  branchesCount: number;
  usersCount: number;
}

export interface INotificationRead {
  id?: number;
  notificationId: number;
  readAt: Date | null;
  dismissedAt: Date | null;
  userId: number;
  expiresAt: Date | null;
}

export interface INotificationReadingInfo {
  usersCount: number;
  readCount: number;
}

export type INotificationReads = INotificationRead[];
export type INotificationRecipientType = "global" | "customer" | "branch";

export enum NotificationType {
  CONCLUDING_LISTS = "concluding_lists",
  SUPPORT_TICKET_UPDATE = "support_ticket_update",
  ORDER_REMINDER = "order_reminder"
}
